import Image from 'next/image'
import Link from 'next/link'

import { pxToRem } from '@/shared/styles/themes'
import { Box, styled, Typography } from '@mui/material'

import type { Author } from '@/slices/blog/mocks/authors'

interface AuthorAvatarProps {
  author: Author
  size?: 'small' | 'medium' | 'large'
  showName?: boolean
  clickable?: boolean
}

export default function AuthorAvatar({
  author,
  size = 'medium',
  showName = true,
  clickable = true,
}: AuthorAvatarProps) {
  const avatarSize = {
    small: 32,
    medium: 48,
    large: 64,
  }[size]

  const content = (
    <AvatarContainer className={clickable ? 'clickable' : ''}>
      <AvatarImage
        src={author.avatar}
        alt={author.name}
        width={avatarSize}
        height={avatarSize}
        className={size}
      />
      {showName && (
        <AuthorDetails>
          <AuthorName variant="body2" className={size}>
            {author.name}
          </AuthorName>
          {size !== 'small' && (
            <AuthorTitle variant="caption">{author.title}</AuthorTitle>
          )}
        </AuthorDetails>
      )}
    </AvatarContainer>
  )

  if (clickable) {
    return (
      <Link href={`/author/${author.id}`} passHref legacyBehavior>
        <AuthorLink>{content}</AuthorLink>
      </Link>
    )
  }

  return content
}

const AvatarContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: pxToRem(12),
  cursor: 'default',
  transition: 'opacity 0.2s ease',
  '&.clickable': {
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.8,
    },
  },
})

const AuthorLink = styled('a')({
  textDecoration: 'none',
  color: 'inherit',
})

const AvatarImage = styled(Image)({
  borderRadius: '50%',
  objectFit: 'cover',
})

const AuthorDetails = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: pxToRem(2),
})

const AuthorName = styled(Typography)(({ theme }) => ({
  fontSize: pxToRem(15),
  fontWeight: 600,
  color: theme.palette.primary.main,
  '&.small': {
    fontSize: pxToRem(14),
  },
  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(16),
    '&.small': {
      fontSize: pxToRem(14),
    },
  },
}))

const AuthorTitle = styled(Typography)(({ theme }) => ({
  fontSize: pxToRem(12),
  color: theme.palette.text.secondary,
  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(13),
  },
}))